import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, Search, Info, Pencil, Check, X, UserMinus, UserCheck, Trash2 } from "lucide-react";
import { api, errMsg, fmtDay } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ReasonDialog } from "@/components/forms/ReasonDialog";
import { DownloadMenu } from "@/components/forms/DownloadMenu";

const sel = "h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2";
const today = () => new Date().toISOString().slice(0, 10);

const PositionPicker = ({ positions, value, onChange, testid }) => {
  const custom = value && !positions.includes(value);
  const [other, setOther] = useState(custom);
  return (
    <div className="space-y-2">
      <select className={sel} value={other ? "__other" : value} onChange={(e) => { const v = e.target.value; setOther(v === "__other"); onChange(v === "__other" ? "" : v); }} data-testid={`${testid}-select`}>
        {positions.map((p) => <option key={p} value={p}>{p}</option>)}<option value="__other">Other title…</option>
      </select>
      {other && <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Custom title" className="bg-white h-10" data-testid={`${testid}-custom`} />}
    </div>
  );
};

const AddDialog = ({ open, onClose, cid, positions, onDone }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [pick, setPick] = useState(null);
  const [f, setF] = useState({ position: "Member", date_joined: today() });
  useEffect(() => { if (open) { setQ(""); setPick(null); setF({ position: "Member", date_joined: today() }); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get(`/committees/${cid}/census-search`, { params: { q } }).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q, open, cid]);
  const save = async () => {
    try { await api.post(`/committees/${cid}/roster`, { census_row_id: pick.row_id, ...f }); toast.success(`${pick.name} added`); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="roster-add-dialog">
        <DialogHeader><DialogTitle>Add member from the Frigate Census</DialogTitle><DialogDescription>Search census names, pick a person, then set their committee position.</DialogDescription></DialogHeader>
        {!pick ? (
          <div className="space-y-2">
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…" className="pl-9 bg-white h-11" data-testid="roster-census-search" /></div>
            <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="roster-census-results">
              {res.length === 0 && <p className="p-4 text-sm text-slate-500">No matching census members.</p>}
              {res.map((r) => (
                <button key={r.row_id} disabled={r.on_committee} onClick={() => setPick(r)} className="w-full text-left px-3 py-3 text-sm hover:bg-slate-50 disabled:opacity-50 flex justify-between" data-testid={`roster-census-pick-${r.row_id}`}>
                  <span className="font-medium text-slate-900">{r.name}</span>{r.on_committee && <span className="text-xs text-slate-500">already on committee</span>}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm flex justify-between items-center" data-testid="roster-picked-name">
              <b className="text-slate-900">{pick.name}</b><button className="text-xs underline text-amber-800" onClick={() => setPick(null)} data-testid="roster-pick-change">change</button></div>
            <label className="block text-xs font-semibold text-slate-600">Position<PositionPicker positions={positions} value={f.position} onChange={(position) => setF({ ...f, position })} testid="roster-add-position" /></label>
            <label className="block text-xs font-semibold text-slate-600">Date joined the committee
              <Input type="date" value={f.date_joined} onChange={(e) => setF({ ...f, date_joined: e.target.value })} className="bg-white h-10 mt-1" data-testid="roster-add-date" /></label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="roster-add-cancel">Cancel</Button>
          <Button disabled={!pick || !f.position.trim()} onClick={save} data-testid="roster-add-save">Add member</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PastDialog = ({ entry, onClose, onSave }) => {
  const [d, setD] = useState(today());
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="roster-past-dialog">
        <DialogHeader><DialogTitle>Mark {entry?.name} as a past member</DialogTitle></DialogHeader>
        <label className="text-xs font-semibold text-slate-600">End date<Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="bg-white h-10 mt-1" data-testid="roster-past-date" /></label>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={() => onSave(d)} data-testid="roster-past-save">Mark past member</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OFFICER = (p) => p !== "Member";

const Row = ({ m, positions, canManage, isSA, onPatch, onPast, onDelete }) => {
  const [edit, setEdit] = useState(null);
  const past = m.status === "past";
  return (
    <div className={`p-3 sm:p-0 sm:grid sm:grid-cols-[1.6fr_1.2fr_1fr_1fr_auto] sm:items-center gap-2 ${past ? "opacity-60" : ""}`} data-testid={`roster-row-${m.entry_id}`}>
      <div className="sm:px-3 sm:py-2.5 min-w-0">
        <div className="font-medium text-slate-900 truncate">{m.name}</div>
        {m.census_note && <div className="text-[11px] text-amber-700 flex items-center gap-1" data-testid={`roster-census-note-${m.entry_id}`}><Info className="w-3 h-3" />{m.census_note}</div>}
      </div>
      <div className="sm:px-3 mt-1 sm:mt-0">
        {edit ? <PositionPicker positions={positions} value={edit.position} onChange={(position) => setEdit({ ...edit, position })} testid="roster-edit-position" />
          : <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${OFFICER(m.position) ? "bg-amber-100 text-amber-900 border border-amber-300" : "bg-slate-100 text-slate-700"}`} data-testid={`roster-position-${m.entry_id}`}>{m.position}</span>}
      </div>
      <div className="sm:px-3 text-xs text-slate-600 mt-1 sm:mt-0">
        {edit ? <Input type="date" value={edit.date_joined} onChange={(e) => setEdit({ ...edit, date_joined: e.target.value })} className="bg-white h-9" data-testid="roster-edit-date" />
          : <>Joined {m.date_joined ? fmtDay(m.date_joined) : "—"}</>}
      </div>
      <div className="sm:px-3 text-xs mt-1 sm:mt-0">
        <span className={`font-semibold ${past ? "text-slate-500" : "text-emerald-700"}`} data-testid={`roster-status-${m.entry_id}`}>{past ? `Past member${m.date_left ? ` · left ${fmtDay(m.date_left)}` : ""}` : "Active"}</span>
      </div>
      <div className="flex gap-1 sm:px-2 mt-2 sm:mt-0 justify-end">
        {canManage && (edit ? <>
          <Button size="icon" variant="ghost" onClick={async () => { if (await onPatch(m, edit)) setEdit(null); }} data-testid="roster-edit-save"><Check className="w-4 h-4 text-emerald-600" /></Button>
          <Button size="icon" variant="ghost" onClick={() => setEdit(null)} data-testid="roster-edit-cancel"><X className="w-4 h-4" /></Button>
        </> : <>
          <Button size="icon" variant="ghost" onClick={() => setEdit({ position: m.position, date_joined: m.date_joined })} data-testid={`roster-edit-${m.entry_id}`}><Pencil className="w-4 h-4" /></Button>
          {past ? <Button size="icon" variant="ghost" title="Make active again" onClick={() => onPatch(m, { status: "active" })} data-testid={`roster-reactivate-${m.entry_id}`}><UserCheck className="w-4 h-4 text-emerald-600" /></Button>
            : <Button size="icon" variant="ghost" title="Mark as past member" onClick={() => onPast(m)} data-testid={`roster-mark-past-${m.entry_id}`}><UserMinus className="w-4 h-4 text-amber-700" /></Button>}
        </>)}
        {isSA && !edit && <Button size="icon" variant="ghost" onClick={() => onDelete(m)} data-testid={`roster-delete-${m.entry_id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
      </div>
    </div>
  );
};

export const MembersTab = ({ cid, info, name, onChange }) => {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("position");
  const [dlg, setDlg] = useState("");
  const [past, setPast] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get(`/committees/${cid}/roster`).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [cid]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChange(); };
  const patch = async (m, body) => { try { await api.patch(`/committees/${cid}/roster/${m.entry_id}`, body); toast.success("Member updated"); changed(); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const out = (list || []).filter((m) => !s || m.name.toLowerCase().includes(s) || m.position.toLowerCase().includes(s));
    return sort === "name" ? [...out].sort((a, b) => (a.status !== b.status ? (a.status === "active" ? -1 : 1) : a.name.localeCompare(b.name))) : out;
  }, [list, q, sort]);
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or position…" className="flex-1 bg-white h-11" data-testid="roster-search-input" />
        <select className={`${sel} sm:w-48 h-11`} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="roster-sort">
          <option value="position">Sort: officers first</option><option value="name">Sort: name</option>
        </select>
        <DownloadMenu label="Download list" path={`/committees/${cid}/roster/export`} filename={`${name} - Members`} disabled={!list?.length} testid="roster-download" />
        {info.can_manage && <Button className="h-11" onClick={() => setDlg("add")} data-testid="roster-add-button"><UserPlus className="w-4 h-4 mr-1" />Add member</Button>}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="roster-list">
        <div className="hidden sm:grid grid-cols-[1.6fr_1.2fr_1fr_1fr_auto] gap-2 bg-slate-100 text-xs font-semibold text-slate-600 rounded-t-lg">
          <div className="px-3 py-2">Name</div><div className="px-3 py-2">Position</div><div className="px-3 py-2">Date joined</div><div className="px-3 py-2">Status</div><div className="px-3 py-2 w-[120px]" />
        </div>
        {!list ? <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          : <div className="divide-y divide-slate-100">
            {shown.length === 0 && <p className="p-10 text-center text-sm text-slate-500" data-testid="roster-empty">No members on this committee yet.</p>}
            {shown.map((m) => <Row key={m.entry_id} m={m} positions={info.positions} canManage={info.can_manage} isSA={info.is_super_admin} onPatch={patch} onPast={setPast} onDelete={setDel} />)}
          </div>}
      </div>
      <AddDialog open={dlg === "add"} onClose={() => setDlg("")} cid={cid} positions={info.positions} onDone={changed} />
      <PastDialog key={past?.entry_id} entry={past} onClose={() => setPast(null)} onSave={async (d) => { if (await patch(past, { status: "past", date_left: d })) setPast(null); }} />
      <ReasonDialog open={!!del} onClose={() => setDel(null)} danger title={`Remove ${del?.name} entirely?`} description="Use 'Mark as past member' to keep history. Removal is recorded in the audit log." confirmLabel="Remove"
        onConfirm={async (reason) => { try { await api.delete(`/committees/${cid}/roster/${del.entry_id}`, { data: { reason } }); toast.success("Member removed"); changed(); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
};
